import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormAttachmentUploadRequirements } from '../attachments/form-attachment-requirements';
import { GeneralFormData } from '../general-form-data.model';
import { FormStep } from '../form-steps.interface';
import { parseLocalDate } from '../../../utils/date-time-formatter';

export class HotWorkPermit {
    id?: string;
    submittedByName: string = '';
    submittedByEmail: string = '';
    submissionDate?: Date;
    date: Date | null = null;

    person1: string = '';
    person2: string = '';
    person3: string = '';
    person4: string = '';
    supervisorForeman: string = '';
    recipientEmail: string = '';
    descriptionOfWork: string = '';
    workLocation1: string = '';
    workLocation2: string = '';
    locateDone: string = '';
    locateDate: Date | null = null;
    utilitiesLocated: string = '';
    utilityProtection: string = '';
    // Requirements Within 35 Feet
    flammableMaterialsRemoved: string = '';
    floorWallOpeningsCovered: string = '';
    sparkPathEvaluated: string = '';
    ductsShutDown: string = '';
    // Drawings Reviewed
    flowDiagrams: string = '';
    undergroundSystems: string = '';
    electricalPneumatic: string = '';
    drainsStorageSystems: string = '';
    // Equipment-Tools-Material
    faceShield: string = '';
    fireExtinguishingEquipment: string = '';
    cottonWoolClothing: string = '';
    fireRetardantClothing: string = '';
    // Items Completed
    linesPurged: string = '';
    linesClean: string = '';
    drainsSewersCovered: string = '';
    valvesClosed: string = '';
    energySystemsLockedOut: string = '';
    blindsInstalled: string = '';
    systemsDeenergized: string = '';
    // Atmospheric Monitoring (Optional Section)
    oxygenLevel: string = '';
    combustibleLevel: string = '';
    carbonMonoxideLevel: string = '';
    hydrogenSulfideLevel: string = '';
    samplingEquipmentType: string = '';
    samplingConductedBy: string = '';
    calibrationCurrent: string = '';
    samplingDate: string = '';
    samplingTime: string = '';
    initiatedBy: string = '';
    approvedBy: string = '';

    constructor(data?: Partial<HotWorkPermit>) {
        // Set default date to today if not provided
        if (data) {
            Object.assign(this, {
                ...data,
                date: data.date ? parseLocalDate(data.date as any) : null,
                submissionDate: data.submissionDate ? new Date(data.submissionDate) : null,
                locateDate: data.locateDate ? parseLocalDate(data.locateDate as any) : null,
                samplingDate: data.samplingDate ? parseLocalDate(data.samplingDate as any) : null
            });
        }
    }

    static getFormInfo(): GeneralFormData {
        return {
            formType: 'hot-work-permit',
            formTitle: 'Hot Work Permit',
            formIcon: 'flame-outline',
            cardImage: 'assets/form-card-tiles/hot-work-permit.png',
           formAttachmentRequirements: {
          
            }
        };
    }

    static getFormSteps(): FormStep[] {
        return [
            {
                id: 'permit-details',
                shortTitle: 'Permit Details',
                title: 'Permit Details & Location',
                icon: 'document-text-outline',
                description: 'Enter permit information, personnel, and work location',
                sections: [
                    {
                        fields: ['date',  'descriptionOfWork', 'supervisorForeman', 'recipientEmail']
                    },
                    {
                        subtitle: 'List of Persons Performing Work',
                        fields: ['person1', 'person2', 'person3', 'person4']
                    },
                    {
                        subtitle:'Location',
                        fields: ['workLocation1', 'workLocation2', 'locateDone', 'locateDate', 'utilitiesLocated', 'utilityProtection']
                    }
            
                ]
            },
            {
                id: 'requirements-35-feet',
                shortTitle: '35 Feet Requirements',
                title: 'Requirements Within 35 Feet',
                icon: 'warning-outline',
                description: 'Complete safety requirements within 35 feet of work area',
                sections: [
                    {
                        fields: ['flammableMaterialsRemoved', 'floorWallOpeningsCovered', 'sparkPathEvaluated', 'ductsShutDown']
                    }
                ]
            },
            {
                id: 'drawings-reviewed',
                shortTitle: 'Drawings Reviewed',
                title: 'Drawings Reviewed',
                icon: 'documents-outline',
                description: 'Confirm review of relevant drawings and systems',
                sections: [
                    {
                        fields: ['flowDiagrams', 'undergroundSystems', 'electricalPneumatic', 'drainsStorageSystems']
                    }
                ]
            },
            {
                id: 'equipment-tools-materials',
                shortTitle: 'Equipment & Tools',
                title: 'Equipment-Tools-Material',
                icon: 'construct-outline',
                description: 'Verify required equipment and protective materials',
                sections: [
                    {
                        fields: ['faceShield', 'fireExtinguishingEquipment', 'cottonWoolClothing', 'fireRetardantClothing']
                    }
                ]
            },
            {
                id: 'items-completed',
                shortTitle: 'Items Completed',
                title: 'Items Completed',
                icon: 'list-circle-outline',
                description: 'Confirm completion of preparatory safety items',
                sections: [
                    {
                        fields: ['linesPurged', 'linesClean', 'drainsSewersCovered', 'valvesClosed', 'energySystemsLockedOut', 'blindsInstalled', 'systemsDeenergized']
                    }
                ]
            },
            {
                id: 'atmospheric-monitoring',
                shortTitle: 'Atmospheric',
                title: 'Atmospheric Monitoring',
                icon: 'analytics-outline',
                description: 'This Section Only Need to be Completed if a Hazardous Atmosphere is Possible or in a Confined Space',
                sections: [
                    {
                        fields: ['oxygenLevel', 'combustibleLevel', 'carbonMonoxideLevel', 'hydrogenSulfideLevel', 'samplingEquipmentType', 'samplingConductedBy', 'calibrationCurrent', 'samplingDate', 'samplingTime', 'wordPressSamplingDate', 'wordPressSamplingTime']
                    }
                ]
            },
            {
                id: 'approvals',
                shortTitle: 'Approvals',
                title: 'Permit Approvals',
                icon: 'pencil-outline',
                description: 'Special Instructions for Utility Work: All utility work performed outside the building requires Supervisor visit and approval. The Safety Department must be called and approve any hot work within 5 feet of utilities.',
                sections: [
                    {
                        fields: ['initiatedBy', 'approvedBy']
                    }
                ]
            }
        ];
    }

    static getFieldMappings(): Record<string, string> {
        return {
            submittedByName: "Submitter Name",
            submittedByEmail: "Submitter Email",
            submissionDate: "Submission Date",
            date: "Date",
            person1: "Person #1",
            person2: "Person #2",
            person3: "Person #3",
            person4: "Person #4",
            supervisorForeman: "Supervisor/Foreman",
            recipientEmail: "Enter the Email Address of Who Should Receive this Hot Work Permit",
            descriptionOfWork: "Description of Work",
            workLocation1: "Location of Work #1",
            workLocation2: "Location of Work #2",
            locateDone: "Was Locate Done?",
            locateDate: "When was the Locate Done?",
            utilitiesLocated: "What Utilities were Located?",
            utilityProtection: "How will the Utilities be Protected from Heat Damage?",
            // Requirements Within 35 Feet
            flammableMaterialsRemoved: "Flammable Materials Removed",
            floorWallOpeningsCovered: "Floor/Wall Openings Covered",
            sparkPathEvaluated: "Path of Sparks Evaluated for Combustibles",
            ductsShutDown: "Cover or Shut Down Ducts that Could Carry Sparks to Combustibles",
            // Drawings Reviewed
            flowDiagrams: "Flow Diagrams",
            undergroundSystems: "Underground Systems",
            electricalPneumatic: "Electrical & Pneumatic",
            drainsStorageSystems: "Drains, Storage, Systems",
            // Equipment-Tools-Material
            faceShield: "Face Shield",
            fireExtinguishingEquipment: "Fire Extinguishing Equipment",
            cottonWoolClothing: "Workers are Wearing Cotton or Wool Clothing (No Synthetics)",
            fireRetardantClothing: "Fire Retardant Rated Outer Clothing Worn",
            // Items Completed
            linesPurged: "Lines/Vessels/Systems Purged",
            linesClean: "Lines/Vessels/Systems Clean",
            drainsSewersCovered: "Drains and Sewers Covered",
            valvesClosed: "Valves Closed or Safe Position",
            energySystemsLockedOut: "Energy Systems Locked Out",
            blindsInstalled: "Blinds Installed",
            systemsDeenergized: "Systems De-energized, Isolated",
            // Atmospheric Monitoring
            oxygenLevel: "Oxygen (O2) %",
            combustibleLevel: "Combustible % of LEL",
            carbonMonoxideLevel: "Carbon Monoxide (CO) PPM",
            hydrogenSulfideLevel: "Hydrogen Sulfide (H2S) PPM",
            samplingEquipmentType: "Type of Sampling Equipment",
            samplingConductedBy: "Sampling Conducting by",
            calibrationCurrent: "Calibration Current: Yes/No (If no stop and get a new gas monitor)",
            samplingDate: "Date",
            samplingTime: "Time",
            wordPressSamplingDate: "Date",
            wordPressSamplingTime: "Time",
            initiatedBy: "Initiated By",
            approvedBy: "Approved By"
        };
    }


} 